<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue';
    import PreviewButtons from '@shared/components/Editor/PreviewButtons.vue';
    import NavIcons from '@cwd/Navigation/NavIcons.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import { useRoute } from 'vue-router';

    const emit = defineEmits<{
        (ev: 'close'): void;
    }>();

    const route = useRoute();

    const props = defineProps<{
        iframeSrc: string;
        showImageToggle?: boolean;
    }>();

    const visionDeficiency = ref('');

    const visionDeficiencies: { labelKey: string; className: string }[] = [
        { labelKey: 'VisionDeficiencyNormalLabel', className: '' },
        { labelKey: 'VisionDeficiencyProtanopiaLabel', className: 'protanopia' },
        { labelKey: 'VisionDeficiencyProtanomalyLabel', className: 'protanomaly' },
        { labelKey: 'VisionDeficiencyDeuteranopiaLabel', className: 'deuteranopia' },
        { labelKey: 'VisionDeficiencyDeuteranomalyLabel', className: 'deuteranomaly' },
        { labelKey: 'VisionDeficiencyTritanopiaLabel', className: 'tritanopia' },
        { labelKey: 'VisionDeficiencyTritanomalyLabel', className: 'tritanomaly' },
        { labelKey: 'VisionDeficiencyAchromatopsiaLabel', className: 'achromatopsia' },
        { labelKey: 'VisionDeficiencyAchromatomalyLabel', className: 'achromatomaly' },
        { labelKey: 'VisionDeficiencyLowContrastLabel', className: 'low-contrast' },
    ];

    const iframeStyle = computed(() => (visionDeficiency.value ? { filter: `url(/Content/filters/colorblind.svg#${visionDeficiency.value})` } : {}));

    const iframe = ref<HTMLIFrameElement>();

    function toggleImages() {
        const doc = iframe.value?.contentDocument;
        if (!doc) {
            console.warn('toggleImages: contentDocument not accessible (cross-origin iframe?)');
            return;
        }
        const imgs = doc.querySelectorAll('img');
        for (const img of imgs) {
            const src = img.getAttribute('src') ?? '';
            if (src !== '') {
                img.setAttribute('data-stored-src', src);
                img.removeAttribute('src');
            } else {
                const stored = img.getAttribute('data-stored-src');
                if (stored) img.setAttribute('src', stored);
            }
        }
    }

    onMounted(async () => {
        watch(
            () => route?.name,
            () => {
                emit('close');
            }
        );
    });
</script>

<template>
    <Teleport to="body">
        <div class="slide-in-dialog slide-in-new" :class="{ in: iframeSrc }">
            <div class="editor-container editor-bg d-flex">
                <div class="canvas-container">
                    <div class="canvas-container-toolbar d-flex gap-sm p-relative">
                        <button type="button" class="btn btn-subtle m-auto-r" @click="$emit('close')" v-tooltip.left="$Resources.TemplatesClosePreviewBtnText">
                            <NavIcons icon="close" :size="20"></NavIcons>
                            <span class="sr-only">{{ $Resources.TemplatesClosePreviewBtnText }}</span>
                        </button>

                        <BSDropdownButton btnClass="btn-subtle" menuClass="dropdown-menu-right">
                            <template #toggle>
                                {{ $Resources.PreviewOptionsButton }}
                                <b class="caret"></b>
                            </template>
                            <template #content>
                                <li v-if="props.showImageToggle">
                                    <a href="javascript:void(0)" @click="toggleImages">
                                        <i class="fa fa-image"></i>
                                        {{ $Resources.PreviewImagesLabel }}
                                    </a>
                                </li>
                                <li class="dropdown-header">{{ $Resources.VisionDeficiencyFilterHeader }}</li>
                                <li v-for="d in visionDeficiencies" :key="d.className">
                                    <a href="javascript:void(0)" @click="visionDeficiency = d.className">
                                        {{ $Resources[d.labelKey] }}
                                    </a>
                                </li>
                            </template>
                        </BSDropdownButton>

                        <slot name="toolbar"></slot>
                    </div>

                    <PreviewButtons></PreviewButtons>

                    <div class="canvas-wrap" :style="iframeStyle">
                        <iframe ref="iframe" :src="iframeSrc" frameborder="0" style="width: 100%; height: 100%" :title="$Resources.PreviewLabel"></iframe>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style lang="less">
    .slide-in-new {
        max-width: 900px;
    }

    body:has(.slide-in-new) {
        overflow: auto !important;
    }

    .canvas-container-toolbar {
        background-color: var(--bg-color);
        padding: var(--ug-spacing-sm) var(--ug-spacing-md);
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
        z-index: 1;
    }
</style>

